import React, { useMemo } from 'react';

import { type MessageDescriptor, useIntl } from 'react-intl-next';

import { JiraIcon, JiraServiceManagementIcon } from '@atlaskit/logo';
import { fg } from '@atlaskit/platform-feature-flags';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';
import { AnyAri } from '@atlassian/ari/any-ari';
import {
	isCommentEntity,
	isProjectEntity,
	isSearchResultGraphEntity,
	isWorkItemEntity,
	type SearchPageNode,
	type SearchResultEntity,
	type SearchResultGraphDocument,
} from '@atlassian/search-client';

import { ProductKeys } from '../../../../../../common/constants/products';
import { LastModified } from '../../../../../../common/ui/last-modified';
import { RestrictedLozenge } from '../../../../../../common/ui/restricted-lozenge';
import SearchMetadataLink from '../../../../../../common/ui/search-metadata-link';
import { SearchResult } from '../../../../../../common/ui/search-result';
import { type AdvancedSearchResultInteractedMetadata } from '../../../../../../common/utils/events/advanced-search-result-interacted';
import {
	enrichExcerpt,
	isNonPublic,
	processExcerpt,
} from '../../../../../../common/utils/search-result-utils';
import { useFireContextMenuButtonClicked } from '../../../../../../controllers/events/use-fire-context-menu-button-clicked';
import { useFireContextMenuCopyButtonClicked } from '../../../../../../controllers/events/use-fire-context-menu-copy-button-clicked';
import { useFireCopyLinkButtonClicked } from '../../../../../../controllers/events/use-fire-copy-link-button-clicked';
import { useFireResultClicked } from '../../../../../../controllers/events/use-fire-result-clicked';
import { useFireResultCtxMenuClicked } from '../../../../../../controllers/events/use-fire-result-context-menu-clicked';
import { useFireSummarizeInChatButtonClicked } from '../../../../../../controllers/events/use-fire-summarize-in-chat-button-clicked';
import { useApplicationMode, useBootstrap } from '../../../../../../controllers/store/bootstrap';
import { usePrimaryProduct } from '../../../../../../controllers/store/bootstrap/accessors';

import {
	JiraDCProjectSearchResult,
	JiraDCWorkItemSearchResult,
} from './jira-dc-issues-search-result';
import { messages } from './messages';

const JIRA_DC_INTEGRATION_IDS = [
	'ari:cloud:platform::integration/jira-dc',
	'ari:cloud:platform::integration/jira-dc-secondary',
	'ari:cloud:platform::integration/jira-dc-tertiary',
];

export const isSearchJiraDcPartial = (partial: SearchPageNode): boolean => {
	return (
		isSearchResultGraphEntity(partial) &&
		JIRA_DC_INTEGRATION_IDS.includes(partial?.integrationId ?? '')
	);
};

const footerContainer = xcss({
	paddingTop: 'space.100',
});

const typeLabelContainer = xcss({
	'::first-letter': {
		textTransform: 'capitalize',
	},
});

interface JiraDcSearchResultProps {
	partial: SearchResultGraphDocument<SearchResultEntity>;
	position?: number;
}

const PROJECT_SUBTYPES = [
	'software',
	'service_desk',
	'business',
	'project', //to be removed once implementation is finalised
];

const SERVICE_DESK = 'service_desk';

const SUBTYPE_PROJECT = 'project';
const SUBTYPE_COMMENT = 'comment';
const SUBTYPE_ISSUE = 'issue';

const JIRA_DC_LABELS: Record<string, MessageDescriptor> = {
	[SUBTYPE_PROJECT]: messages.project,
	[SUBTYPE_COMMENT]: messages.comment,
	[SUBTYPE_ISSUE]: messages.issue,
};

const getLabel = (subtype: string): MessageDescriptor => {
	if (PROJECT_SUBTYPES.includes(subtype)) {
		return messages.project;
	}
	return JIRA_DC_LABELS[subtype];
};

const isJsmIcon = (containerType?: string) => {
	return fg('is_jsm_dc_enabled_in_ui') && containerType?.toLowerCase() === SERVICE_DESK;
};

export const JiraDcSearchResult = ({
	partial,
	position,
}: JiraDcSearchResultProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const [primaryProduct] = usePrimaryProduct();
	const [{ cloudId, thirdPartyConfigs }] = useBootstrap();
	const appMode = useApplicationMode();
	const resultAri = AnyAri.parse(partial.id);
	const metadata = useMemo(() => {
		return {
			id: partial.id,
			containerId: null,
			spaceId: null,
			contentType: partial.subtype,
			entityType: resultAri.resourceType,
			contentId: resultAri.resourceId,
			product: ProductKeys.JiraDC,
		} satisfies AdvancedSearchResultInteractedMetadata;
	}, [partial, resultAri]);

	const onResultClicked = useFireResultClicked({
		metadata,
		position,
	});

	const onResultCtxMenuClicked = useFireResultCtxMenuClicked({
		metadata,
		position,
	});
	const onCopyLinkButtonClicked = useFireCopyLinkButtonClicked('searchResultTitle');
	const onSummarizeInChatButtonClicked = useFireSummarizeInChatButtonClicked({
		location: 'searchResult',
		metadata,
		position,
	});
	const onContextMenuCopyButtonClicked = useFireContextMenuCopyButtonClicked();
	const onContextMenuButtonClicked = useFireContextMenuButtonClicked();

	const { lastModifiedDate, url, permissionLevel, title, excerpt } = partial;

	const lastModifiedComponent = lastModifiedDate ? (
		<LastModified lastModified={lastModifiedDate} />
	) : null;

	const jiraIcon = <JiraIcon size="medium" shouldUseNewLogoDesign />;
	const jsmIcon = <JiraServiceManagementIcon size="medium" shouldUseNewLogoDesign />;
	let icon = jiraIcon;

	// Find the specific datasource for this result based on its integrationId/providerId
	const resultDatasource =
		thirdPartyConfigs?.[metadata.product]?.datasources?.find(
			(ds) => ds.integrationId === partial.integrationId,
		) || null;

	// Use the result's datasource workspace name, fallback to main config
	const site =
		resultDatasource?.workspaceName || thirdPartyConfigs?.[metadata.product]?.workspaceName;

	const entityToLabelMap: Record<string, string> = {
		space: 'space',
		'work-item': 'issue',
		project: 'issue',
		comment: 'comment',
	};

	const resultType =
		partial.subtype || (entityToLabelMap[metadata.entityType] ?? metadata.entityType);

	const labelMessage = getLabel(resultType);
	const label = labelMessage ? formatMessage(labelMessage) : resultType;

	const isNotPublic = isNonPublic(permissionLevel);

	if (isProjectEntity(partial)) {
		if (isJsmIcon(partial.entity?.container?.subtype)) {
			icon = jsmIcon;
		}
		return (
			<JiraDCProjectSearchResult
				partial={partial}
				position={position}
				icon={icon}
				product={metadata.product}
				onResultClicked={onResultClicked}
				onResultCtxMenuClicked={onResultCtxMenuClicked}
			/>
		);
	}

	if (isWorkItemEntity(partial)) {
		if (isJsmIcon(partial.entity?.container?.subtype)) {
			icon = jsmIcon;
		}
		return (
			<JiraDCWorkItemSearchResult
				partial={partial}
				position={position}
				icon={icon}
				product={metadata.product}
				onResultClicked={onResultClicked}
				onResultCtxMenuClicked={onResultCtxMenuClicked}
			/>
		);
	}

	let container: JSX.Element | string | undefined;
	if (isCommentEntity(partial)) {
		if (isJsmIcon(partial.entity?.container?.subtype)) {
			icon = jsmIcon;
		}
		const { entity } = partial;
		const containerName = entity?.container?.displayName;
		const containerUrl = entity?.container?.url;
		container =
			containerUrl && containerName ? (
				<SearchMetadataLink label={containerName} href={containerUrl} />
			) : (
				containerName
			);
	} else {
		if (isJsmIcon(partial.subtype)) {
			icon = jsmIcon;
		}
	}

	const headerMetadataParts = [
		['label', <Box xcss={typeLabelContainer}>{label}</Box>],
		['site', site],
		['container', container],
		['last-modified', lastModifiedComponent],
	];

	// space noun (project in the UI) and comment use default result component
	return (
		<SearchResult
			id={partial.id}
			product={metadata.product}
			productSelected
			onResultClicked={onResultClicked}
			onResultContextMenu={onResultCtxMenuClicked}
			onCopyLinkButtonClicked={onCopyLinkButtonClicked}
			onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
			onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
			onContextMenuButtonClicked={onContextMenuButtonClicked}
			description={processExcerpt(excerpt)}
			icon={icon}
			headerMetadataParts={headerMetadataParts}
			footer={
				isNotPublic && (
					<Box xcss={footerContainer}>
						<RestrictedLozenge restriction={permissionLevel} />
					</Box>
				)
			}
			title={enrichExcerpt(title)}
			url={url}
			isThirdParty
			cloudId={cloudId}
			primaryProduct={primaryProduct}
			position={position}
			partial={partial}
			appMode={appMode}
		/>
	);
};
